import { type RefObject, useMemo, useState } from 'react';

import { offset, type VirtualElement } from '@floating-ui/react';

import { NAVBAR_MENU_ITEM_BORDER_DEFAULTS } from '../../NavbarMenuItem.constants';
import type { NavbarMenuItemV2ElementProps } from '../../NavbarMenuItem.type';
import { Accordion } from './Accordion';
import { FloatingDropdown } from './FloatingDropdown';

export function NavbarMenuItemDropdownV2(
  props: NavbarMenuItemV2ElementProps & {
    controlbyselected: boolean | string;
    labelComponent?: React.ReactNode;
    showCaret?: boolean;
    evaluatedHoverBorderRadius?: string | number;
    forceShowAccordion?: boolean;
    virtualElementRef?: RefObject<VirtualElement | undefined>;
  },
) {
  const [isOpen, setIsOpen] = useState(false);
  const { element, screen, attributes, children, forceShowAccordion, virtualElementRef, isInEditor = false } = props;
  const { attrs } = element;

  const isUserButton = attrs?.type === 'user_button';
  const isDropdown = attrs?.type === 'dropdown';
  const isButton = isUserButton && attrs?.style === 'button';

  const evaluatedHoverBorderRadius =
    (props.evaluatedHoverBorderRadius ?? isDropdown)
      ? (attrs?.hoverBorderRadius ?? attrs?.borderRadius ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius)
      : undefined;

  const middlewares = useMemo(() => {
    if (isInEditor) return [];
    if (isUserButton && screen === 'desktop') return [offset({ crossAxis: -8 })];
    return [];
  }, [isInEditor, isUserButton, screen]);

  if ((!isDropdown && !isUserButton) || (isUserButton && isButton)) {
    return null;
  }

  const shouldForceOpen = isInEditor && attributes?.['data-is-open'] === 'true';

  // ======= Accordion =======
  if (screen === 'mobile' && forceShowAccordion) {
    return (
      <Accordion
        {...props}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isInEditor={isInEditor}
        shouldForceOpen={shouldForceOpen}
        evaluatedHoverBorderRadius={evaluatedHoverBorderRadius}
      >
        {children}
      </Accordion>
    );
  }

  // ======= Floating Dropdown =======
  return (
    <FloatingDropdown
      {...props}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      shouldForceOpen={shouldForceOpen}
      isInEditor={isInEditor}
      middlewares={middlewares}
      showOnClick={screen === 'mobile'}
      evaluatedHoverBorderRadius={evaluatedHoverBorderRadius}
      virtualElementRef={virtualElementRef}
    >
      {children}
    </FloatingDropdown>
  );
}
